/**
 * ReportForm — universal form-fill component for all 3 staff report types.
 *
 * data-testid: candeza-reports-staff-form-*
 *
 * - Period dropdown options come from PERIOD_TYPES (never hard-coded)
 * - Max-drafts count comes from MAX_DRAFTS_PER_USER
 * - Autosave on form changes (debounced 2s); explicit "Save Draft" button
 * - Submit triggers POST /submit; does NOT return a PDF blob
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Button } from '@/components/common';
import { api } from '@core/api/client';
import {
  useCreateStaffReport,
  useUpdateStaffReport,
  useSubmitStaffReport,
  type ReportType,
  type PeriodType,
  type StaffReport,
} from './api';

// 11E.8 — student search-select (lifted from the orphaned
// src/features/reports/student/StudentReport.tsx, which had this pattern
// built but was never wired into a route). Without a real student_id on
// the payload, /v1/reports/staff/instructor?student_id= filtering (the
// Grades tab, the session-page deep-link, the viewer portal) returns
// nothing for reports created through this form — this is a correctness
// fix, not new scope.
interface StudentSearchResult {
  id: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
}

// Config constants (matches backend rules.py)
const PERIOD_TYPES: PeriodType[] = ['week', 'biweek', 'month', 'bimonth', 'quarter'];
const MAX_DRAFTS_PER_USER = 10;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Section = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionBody = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Row = styled.div<{ cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols ?? 1}, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 0; }
`;

const SearchWrapper = styled.div`
  position: relative;
`;

const StudentDropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  max-height: 220px;
  overflow-y: auto;
  list-style: none;
  margin: 2px 0 0;
  padding: 4px 0;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const StudentDropdownItem = styled.li`
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #f5f3ff;
    color: #6d28d9;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  min-height: 80px;
  resize: vertical;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 0; }
`;

const Select = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const AutosaveIndicator = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  align-self: center;
`;

const DraftsBadge = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  background: #fef3c7;
  color: #92400e;
  padding: 2px 8px;
  border-radius: 10px;
`;

// ---------------------------------------------------------------------------
// Sub-form components per report type
// ---------------------------------------------------------------------------

function InstructorFields({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (key: string, val: unknown) => void;
}) {
  const get = (k: string, def: unknown = '') => data[k] ?? def;

  return (
    <>
      <Section data-testid="candeza-reports-staff-form-section-progress">
        <SectionHeader>📚 Progress &amp; Development</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Theory Covered</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-theory-covered"
                value={String(get('theory_covered'))}
                onChange={(e) => onChange('theory_covered', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Pieces Covered</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-pieces-covered"
                value={String(get('pieces_covered'))}
                onChange={(e) => onChange('pieces_covered', e.target.value)}
              />
            </Field>
          </Row>
          <Field>
            <Label>Technical Progress</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-technical-progress"
              value={String(get('technical_progress'))}
              onChange={(e) => onChange('technical_progress', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Skills</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-skills"
              value={String(get('skills'))}
              onChange={(e) => onChange('skills', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      <Section data-testid="candeza-reports-staff-form-section-ratings">
        <SectionHeader>⭐ Overall Progress Ratings (1–5)</SectionHeader>
        <SectionBody>
          {(['technical_skills', 'creativity', 'consistency', 'attitude', 'overall'] as const).map((key) => (
            <Row key={key} cols={2}>
              <Field>
                <Label>{key.replace('_', ' ')}</Label>
                <Input
                  type="number"
                  min={0}
                  max={5}
                  data-testid={`candeza-reports-staff-form-rating-${key}`}
                  value={Number(get(`${key}_rating`, 0))}
                  onChange={(e) => onChange(`${key}_rating`, Number(e.target.value))}
                />
              </Field>
              <Field>
                <Label>Remarks</Label>
                <Input
                  type="text"
                  data-testid={`candeza-reports-staff-form-remarks-${key}`}
                  value={String(get(`${key}_remarks`))}
                  onChange={(e) => onChange(`${key}_remarks`, e.target.value)}
                />
              </Field>
            </Row>
          ))}
        </SectionBody>
      </Section>
    </>
  );
}

function OfficeManagerFields({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (key: string, val: unknown) => void;
}) {
  const get = (k: string, def: unknown = '') => data[k] ?? def;

  return (
    <>
      <Section data-testid="candeza-reports-staff-form-section-instructor-attendance">
        <SectionHeader>👥 Instructor Attendance</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Summary</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-instructor-attendance"
              value={String(get('instructor_attendance_summary'))}
              onChange={(e) => onChange('instructor_attendance_summary', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Remarks</Label>
            <Input
              type="text"
              data-testid="candeza-reports-staff-form-attendance-remarks"
              value={String(get('attendance_remarks'))}
              onChange={(e) => onChange('attendance_remarks', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      <Section data-testid="candeza-reports-staff-form-section-google-reviews">
        <SectionHeader>⭐ Google Reviews</SectionHeader>
        <SectionBody>
          <Row cols={3}>
            <Field>
              <Label>Collected This Period</Label>
              <Input
                type="number"
                data-testid="candeza-reports-staff-form-reviews-collected"
                value={Number(get('reviews_collected_this_period', 0))}
                onChange={(e) => onChange('reviews_collected_this_period', Number(e.target.value))}
              />
            </Field>
            <Field>
              <Label>Total Google Reviews</Label>
              <Input
                type="number"
                data-testid="candeza-reports-staff-form-total-reviews"
                value={Number(get('total_google_reviews', 0))}
                onChange={(e) => onChange('total_google_reviews', Number(e.target.value))}
              />
            </Field>
            <Field>
              <Label>Current Rating</Label>
              <Input
                type="number"
                step={0.1}
                min={0}
                max={5}
                data-testid="candeza-reports-staff-form-google-rating"
                value={Number(get('current_google_rating', 0))}
                onChange={(e) => onChange('current_google_rating', Number(e.target.value))}
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      <Section data-testid="candeza-reports-staff-form-section-feedback">
        <SectionHeader>💬 Overall Feedback</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Positive Feedback</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-positive-feedback"
              value={String(get('positive_feedback_summary'))}
              onChange={(e) => onChange('positive_feedback_summary', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Concerns</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-concerns"
              value={String(get('concerns_summary'))}
              onChange={(e) => onChange('concerns_summary', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Action Items</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-feedback-actions"
              value={String(get('feedback_action_items'))}
              onChange={(e) => onChange('feedback_action_items', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      <Section data-testid="candeza-reports-staff-form-section-parent-concerns">
        <SectionHeader>👨‍👩‍👧 Parent Concerns</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Parent Concerns</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-parent-concerns"
              value={String(get('parent_concerns'))}
              onChange={(e) => onChange('parent_concerns', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      <Section data-testid="candeza-reports-staff-form-section-office-needs">
        <SectionHeader>🏢 Office &amp; Materials Needs</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Office Needs</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-office-needs"
                value={String(get('office_needs'))}
                onChange={(e) => onChange('office_needs', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Materials Needs</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-materials-needs"
                value={String(get('materials_needs'))}
                onChange={(e) => onChange('materials_needs', e.target.value)}
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      <Section data-testid="candeza-reports-staff-form-section-support">
        <SectionHeader>🆘 Other Support</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Support Needed</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-other-support"
              value={String(get('other_support_needed'))}
              onChange={(e) => onChange('other_support_needed', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Urgency</Label>
            <Input
              type="text"
              data-testid="candeza-reports-staff-form-support-urgency"
              value={String(get('support_urgency'))}
              onChange={(e) => onChange('support_urgency', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>
    </>
  );
}

const BDE_PLATFORMS = ['YouTube', 'Instagram', 'Facebook'] as const;

function BDECheckboxGroup({
  options,
  value,
  onChange,
  testIdPrefix,
}: {
  options: readonly string[];
  value: string[];
  onChange: (val: string[]) => void;
  testIdPrefix: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {options.map((opt) => (
        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <input
            type="checkbox"
            data-testid={`${testIdPrefix}-${opt.toLowerCase()}`}
            checked={value.includes(opt)}
            onChange={(e) => {
              if (e.target.checked) onChange([...value, opt]);
              else onChange(value.filter((v) => v !== opt));
            }}
          />
          {opt}
        </label>
      ))}
    </div>
  );
}

function BDEFields({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (key: string, val: unknown) => void;
}) {
  const get = (k: string, def: unknown = '') => data[k] ?? def;
  const getArr = (k: string): string[] => (Array.isArray(data[k]) ? (data[k] as string[]) : []);

  return (
    <>
      {/* Section 1 */}
      <Section data-testid="candeza-reports-staff-form-section-whatsapp">
        <SectionHeader>📱 WhatsApp Campaigns</SectionHeader>
        <SectionBody>
          <Field>
            <Label>WhatsApp Community Groups</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-whatsapp"
              placeholder="List community groups messaged this period (one per line)"
              value={String(get('whatsapp_community_groups'))}
              onChange={(e) => onChange('whatsapp_community_groups', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 2 */}
      <Section data-testid="candeza-reports-staff-form-section-social-media">
        <SectionHeader>📸 Social Media Campaigns</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Social Media Campaigns</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-social-media"
              placeholder="Describe campaigns run on Instagram, Facebook, YouTube, etc."
              value={String(get('social_media_campaigns'))}
              onChange={(e) => onChange('social_media_campaigns', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 3 */}
      <Section data-testid="candeza-reports-staff-form-section-physical-promotions">
        <SectionHeader>🏪 Physical Promotions</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Physical Promotion Areas</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-physical-promotions"
              placeholder="Apartment complexes, schools, events visited"
              value={String(get('physical_promotion_areas'))}
              onChange={(e) => onChange('physical_promotion_areas', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 4 */}
      <Section data-testid="candeza-reports-staff-form-section-b2b">
        <SectionHeader>🤝 B2B Engagements</SectionHeader>
        <SectionBody>
          <Field>
            <Label>B2B Engagements (optional)</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-b2b"
              placeholder="Corporate tie-ups, school partnerships, etc."
              value={String(get('b2b_engagements'))}
              onChange={(e) => onChange('b2b_engagements', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 5 */}
      <Section data-testid="candeza-reports-staff-form-section-other-opportunities">
        <SectionHeader>💡 Other Opportunities</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Other Opportunities (optional)</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-other-opportunities"
              placeholder="Any other marketing opportunities pursued"
              value={String(get('other_opportunities'))}
              onChange={(e) => onChange('other_opportunities', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 6 */}
      <Section data-testid="candeza-reports-staff-form-section-community">
        <SectionHeader>🏘️ Community Performance</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Top 5 Communities</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-top5"
                value={String(get('top5_communities'))}
                onChange={(e) => onChange('top5_communities', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Bottom 5 Communities (optional)</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-bottom5"
                value={String(get('bottom5_communities'))}
                onChange={(e) => onChange('bottom5_communities', e.target.value)}
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      {/* Section 7 */}
      <Section data-testid="candeza-reports-staff-form-section-student-spotlight">
        <SectionHeader>🎬 Student Spotlight Videos</SectionHeader>
        <SectionBody>
          <Row cols={3}>
            <Field>
              <Label>Dance</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-student-spotlight-dance"
                placeholder="Video link"
                value={String(get('student_spotlight_dance'))}
                onChange={(e) => onChange('student_spotlight_dance', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Instruments</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-student-spotlight-instruments"
                placeholder="Video link"
                value={String(get('student_spotlight_instruments'))}
                onChange={(e) => onChange('student_spotlight_instruments', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Vocals</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-student-spotlight-vocals"
                placeholder="Video link"
                value={String(get('student_spotlight_vocals'))}
                onChange={(e) => onChange('student_spotlight_vocals', e.target.value)}
              />
            </Field>
          </Row>
          <Field>
            <Label>Platforms</Label>
            <BDECheckboxGroup
              options={BDE_PLATFORMS}
              value={getArr('spotlight_platforms')}
              onChange={(val) => onChange('spotlight_platforms', val)}
              testIdPrefix="candeza-reports-staff-form-spotlight-platform"
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 8 */}
      <Section data-testid="candeza-reports-staff-form-section-instructor-spotlight">
        <SectionHeader>🎤 Instructor Spotlight Videos</SectionHeader>
        <SectionBody>
          <Row cols={3}>
            <Field>
              <Label>Dance</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-instructor-spotlight-dance"
                placeholder="Video link"
                value={String(get('instructor_spotlight_dance'))}
                onChange={(e) => onChange('instructor_spotlight_dance', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Instruments</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-instructor-spotlight-instruments"
                placeholder="Video link"
                value={String(get('instructor_spotlight_instruments'))}
                onChange={(e) => onChange('instructor_spotlight_instruments', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Vocals</Label>
              <TextArea
                data-testid="candeza-reports-staff-form-instructor-spotlight-vocals"
                placeholder="Video link"
                value={String(get('instructor_spotlight_vocals'))}
                onChange={(e) => onChange('instructor_spotlight_vocals', e.target.value)}
              />
            </Field>
          </Row>
          <Field>
            <Label>Platforms</Label>
            <BDECheckboxGroup
              options={BDE_PLATFORMS}
              value={getArr('instructor_spotlight_platforms')}
              onChange={(val) => onChange('instructor_spotlight_platforms', val)}
              testIdPrefix="candeza-reports-staff-form-instructor-spotlight-platform"
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 9 */}
      <Section data-testid="candeza-reports-staff-form-section-music-room-showcase">
        <SectionHeader>🎵 Music Room Showcase</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Showcase Link</Label>
              <Input
                type="text"
                data-testid="candeza-reports-staff-form-music-room-showcase-link"
                placeholder="https://youtube.com/..."
                value={String(get('music_room_showcase_link'))}
                onChange={(e) => onChange('music_room_showcase_link', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Showcase Date</Label>
              <Input
                type="date"
                data-testid="candeza-reports-staff-form-music-room-showcase-date"
                value={String(get('music_room_showcase_date'))}
                onChange={(e) => onChange('music_room_showcase_date', e.target.value)}
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      {/* Section 10 */}
      <Section data-testid="candeza-reports-staff-form-section-poster-display">
        <SectionHeader>🪧 Poster Display</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            {(['birthday_poster', 'festivals_poster', 'music_room_poster', 'google_review_slideshow'] as const).map((field) => (
              <Field key={field}>
                <Label>{field.replace(/_/g, ' ')}</Label>
                <Select
                  data-testid={`candeza-reports-staff-form-${field.replace(/_/g, '-')}`}
                  value={String(get(field))}
                  onChange={(e) => onChange(field, e.target.value)}
                >
                  <option value="">— select —</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="N/A">N/A</option>
                </Select>
              </Field>
            ))}
          </Row>
        </SectionBody>
      </Section>

      {/* Section 11 */}
      <Section data-testid="candeza-reports-staff-form-section-google-page-updates">
        <SectionHeader>🌐 Google Page Updates</SectionHeader>
        <SectionBody>
          <Row cols={2}>
            <Field>
              <Label>Facility Photos</Label>
              <Select
                data-testid="candeza-reports-staff-form-facility-photos"
                value={String(get('facility_photos'))}
                onChange={(e) => onChange('facility_photos', e.target.value)}
              >
                <option value="">— select —</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
            </Field>
            <Field>
              <Label>Promotion Posters</Label>
              <Select
                data-testid="candeza-reports-staff-form-promotion-posters"
                value={String(get('promotion_posters'))}
                onChange={(e) => onChange('promotion_posters', e.target.value)}
              >
                <option value="">— select —</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
            </Field>
          </Row>
          <Field>
            <Label>Important Announcements</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-important-announcements"
              value={String(get('important_announcements'))}
              onChange={(e) => onChange('important_announcements', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>

      {/* Section 12 */}
      <Section data-testid="candeza-reports-staff-form-section-support">
        <SectionHeader>🆘 Support Needed</SectionHeader>
        <SectionBody>
          <Field>
            <Label>Support Needed (optional)</Label>
            <TextArea
              data-testid="candeza-reports-staff-form-support-needed"
              value={String(get('support_needed'))}
              onChange={(e) => onChange('support_needed', e.target.value)}
            />
          </Field>
        </SectionBody>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main ReportForm
// ---------------------------------------------------------------------------

interface ReportFormProps {
  reportType: ReportType;
  existingDraft?: StaffReport | null;
  draftCount?: number;
  onSaved?: (report: StaffReport) => void;
  onSubmitted?: (report: StaffReport) => void;
  /** 11E.8 — session-page "Grades / Add grades" deep-link pre-fill
   * (src/routes/sessionRegister.tsx). Ignored when `existingDraft` is set. */
  prefillStudentId?: string;
  prefillStudentName?: string;
  prefillPeriodStart?: string;
  prefillPeriodEnd?: string;
}

export const ReportForm: React.FC<ReportFormProps> = ({
  reportType,
  existingDraft,
  draftCount = 0,
  onSaved,
  onSubmitted,
  prefillStudentId,
  prefillStudentName,
  prefillPeriodStart,
  prefillPeriodEnd,
}) => {
  const [draftId, setDraftId] = useState<string | null>(existingDraft?.id ?? null);
  const [formData, setFormData] = useState<Record<string, unknown>>(existingDraft?.form_data ?? {});
  const [periodType, setPeriodType] = useState<PeriodType>(existingDraft?.period_type ?? 'month');
  const [periodStart, setPeriodStart] = useState(
    existingDraft?.period_start ?? prefillPeriodStart ?? '',
  );
  const [periodEnd, setPeriodEnd] = useState(existingDraft?.period_end ?? prefillPeriodEnd ?? '');
  const [studentId, setStudentId] = useState(existingDraft?.student_id ?? prefillStudentId ?? '');
  const [studentName, setStudentName] = useState(
    existingDraft?.student_name ?? prefillStudentName ?? '',
  );
  const [studentSearchResults, setStudentSearchResults] = useState<StudentSearchResult[]>([]);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const studentSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [instructorName, setInstructorName] = useState(existingDraft?.instructor_name ?? '');
  const [discipline, setDiscipline] = useState(existingDraft?.discipline ?? '');
  const [level, setLevel] = useState(existingDraft?.level ?? '');
  const [autosaving, setAutosaving] = useState(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleStudentSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setStudentName(q);
    setStudentId('');
    setShowStudentDropdown(true);
    if (studentSearchTimer.current) clearTimeout(studentSearchTimer.current);
    if (q.length < 1) {
      setStudentSearchResults([]);
      return;
    }
    studentSearchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get<{ items: StudentSearchResult[] }>(
          `/v1/students?search=${encodeURIComponent(q)}&limit=10`,
        );
        setStudentSearchResults(res.items ?? []);
      } catch {
        setStudentSearchResults([]);
      }
    }, 300);
  }, []);

  const handleSelectStudent = useCallback((s: StudentSearchResult) => {
    const name = s.display_name ?? `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim();
    setStudentId(s.id);
    setStudentName(name);
    setShowStudentDropdown(false);
    setStudentSearchResults([]);
  }, []);

  const createMutation = useCreateStaffReport(reportType);
  const updateMutation = useUpdateStaffReport(reportType);
  const submitMutation = useSubmitStaffReport(reportType);

  const atDraftLimit = !draftId && draftCount >= MAX_DRAFTS_PER_USER;

  const handleFieldChange = useCallback(
    (key: string, val: unknown) => {
      setFormData((prev) => ({ ...prev, [key]: val }));
    },
    [],
  );

  // Autosave debounce
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (!draftId) return; // Only autosave if we already have a draft

    autosaveTimer.current = setTimeout(async () => {
      setAutosaving(true);
      try {
        await updateMutation.mutateAsync({
          id: draftId,
          data: { form_data: formData, period_type: periodType, period_start: periodStart, period_end: periodEnd },
        });
      } finally {
        setAutosaving(false);
      }
    }, 2000);

    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, periodType, periodStart, periodEnd]);

  const handleSaveDraft = async () => {
    if (atDraftLimit) {
      toast.error(`Max ${MAX_DRAFTS_PER_USER} drafts reached. Discard one to save a new draft.`);
      return;
    }

    const payload = {
      report_type: reportType,
      period_type: periodType,
      period_start: periodStart,
      period_end: periodEnd,
      form_data: formData,
      student_id: studentId || undefined,
      student_name: studentName || undefined,
      instructor_name: instructorName || undefined,
      discipline: discipline || undefined,
      level: level || undefined,
      status: 'draft' as const,
    };

    if (draftId) {
      const updated = await updateMutation.mutateAsync({
        id: draftId,
        data: { form_data: formData, period_type: periodType, period_start: periodStart, period_end: periodEnd },
      });
      toast.success('Draft saved');
      onSaved?.(updated);
    } else {
      const created = await createMutation.mutateAsync(payload);
      setDraftId(created.id);
      toast.success('Draft created');
      onSaved?.(created);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draftId) {
      // Create draft first, then submit
      const payload = {
        report_type: reportType,
        period_type: periodType,
        period_start: periodStart,
        period_end: periodEnd,
        form_data: formData,
        student_id: studentId || undefined,
        student_name: studentName || undefined,
        instructor_name: instructorName || undefined,
        discipline: discipline || undefined,
        level: level || undefined,
        status: 'draft' as const,
      };
      const draft = await createMutation.mutateAsync(payload);
      setDraftId(draft.id);
      const submitted = await submitMutation.mutateAsync(draft.id);
      toast.success('Report submitted!');
      onSubmitted?.(submitted);
    } else {
      const submitted = await submitMutation.mutateAsync(draftId);
      toast.success('Report submitted!');
      onSubmitted?.(submitted);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSubmitting = submitMutation.isPending;

  return (
    <Form onSubmit={handleSubmit} data-testid="candeza-reports-staff-form">
      {atDraftLimit && (
        <div
          data-testid="candeza-reports-staff-form-max-drafts-error"
          style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, padding: '10px 16px', color: '#92400e', fontSize: 13 }}
        >
          You have reached the maximum of {MAX_DRAFTS_PER_USER} drafts. Discard an existing draft to save a new one.
        </div>
      )}

      {/* Period selector */}
      <Section data-testid="candeza-reports-staff-form-section-period">
        <SectionHeader>📅 Report Period</SectionHeader>
        <SectionBody>
          <Row cols={3}>
            <Field>
              <Label>Period Type</Label>
              <Select
                data-testid="candeza-reports-staff-form-period-type"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              >
                {PERIOD_TYPES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Period Start</Label>
              <Input
                type="date"
                data-testid="candeza-reports-staff-form-period-start"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
              />
            </Field>
            <Field>
              <Label>Period End</Label>
              <Input
                type="date"
                data-testid="candeza-reports-staff-form-period-end"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
              />
            </Field>
          </Row>
        </SectionBody>
      </Section>

      {/* Instructor-report extras */}
      {reportType === 'instructor' && (
        <Section data-testid="candeza-reports-staff-form-section-student-info">
          <SectionHeader>📋 Student Information</SectionHeader>
          <SectionBody>
            <Row cols={2}>
              <Field>
                <Label>Student Name</Label>
                <SearchWrapper>
                  <Input
                    type="text"
                    data-testid="candeza-reports-staff-form-student-name"
                    value={studentName}
                    onChange={handleStudentSearchChange}
                    onFocus={() => studentSearchResults.length > 0 && setShowStudentDropdown(true)}
                    onBlur={() => setTimeout(() => setShowStudentDropdown(false), 150)}
                    autoComplete="off"
                    required
                  />
                  {showStudentDropdown && studentSearchResults.length > 0 && (
                    <StudentDropdown data-testid="candeza-reports-staff-form-student-dropdown">
                      {studentSearchResults.map((s) => (
                        <StudentDropdownItem
                          key={s.id}
                          data-testid={`candeza-reports-staff-form-student-option-${s.id}`}
                          onMouseDown={() => handleSelectStudent(s)}
                        >
                          {s.display_name ?? `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim()}
                        </StudentDropdownItem>
                      ))}
                    </StudentDropdown>
                  )}
                </SearchWrapper>
                {studentId && (
                  <span style={{ fontSize: 11, color: '#16a34a' }}>
                    ✓ linked to student record
                  </span>
                )}
              </Field>
              <Field>
                <Label>Instructor Name</Label>
                <Input
                  type="text"
                  data-testid="candeza-reports-staff-form-instructor-name"
                  value={instructorName}
                  onChange={(e) => setInstructorName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Discipline</Label>
                <Input
                  type="text"
                  data-testid="candeza-reports-staff-form-discipline"
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Level</Label>
                <Input
                  type="text"
                  data-testid="candeza-reports-staff-form-level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                />
              </Field>
            </Row>
          </SectionBody>
        </Section>
      )}

      {/* Type-specific fields */}
      {reportType === 'instructor' && (
        <InstructorFields data={formData} onChange={handleFieldChange} />
      )}
      {reportType === 'office-manager' && (
        <OfficeManagerFields data={formData} onChange={handleFieldChange} />
      )}
      {reportType === 'business-dev-executive' && (
        <BDEFields data={formData} onChange={handleFieldChange} />
      )}

      {/* Actions */}
      <Actions>
        {autosaving && <AutosaveIndicator>Autosaving…</AutosaveIndicator>}
        {draftId && (
          <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>
            Draft ID: {draftId.slice(0, 8)}…
          </span>
        )}
        {draftCount > 0 && (
          <DraftsBadge data-testid="candeza-reports-staff-form-draft-count">
            {draftCount}/{MAX_DRAFTS_PER_USER} drafts
          </DraftsBadge>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSaveDraft}
          disabled={isSaving || atDraftLimit}
          data-testid="candeza-reports-staff-form-save-draft"
        >
          {isSaving ? 'Saving…' : '💾 Save Draft'}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={isSubmitting || !periodStart || !periodEnd}
          data-testid="candeza-reports-staff-form-submit"
        >
          {isSubmitting ? 'Submitting…' : '📤 Submit Report'}
        </Button>
      </Actions>
    </Form>
  );
};

